import { SignedIn, SignedOut, SignInButton } from "@clerk/clerk-react";
import { ArrowRight, Menu, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  getLocale,
  getTranslationFn,
  type Locale,
  updateCurrentLocale,
} from "../../i18n";
import { cn } from "../../lib/utils";
import * as Paths from "../../paths";
import { LOCALE_COOKIE_NAME } from "../language-picker";
import { Button } from "../ui/button";

interface MarketingLayoutProps {
  /** Explicit locale (used by landing page routes). When omitted, reads from cookie/localStorage. */
  locale?: Locale;
  children: React.ReactNode;
  /** Nav items displayed in the header. Defaults to landing-page sections. */
  navItems?: { label: string; href: string }[];
}

function setLocaleCookie(locale: Locale) {
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `${LOCALE_COOKIE_NAME}=${locale};path=/;expires=${expires.toUTCString()};SameSite=Lax`;
}

function LanguageSwitcher({
  locale,
  onSwitch,
  className,
}: {
  locale: Locale;
  onSwitch: (newLocale: Locale) => void;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-1 text-sm", className)}>
      <button
        onClick={() => onSwitch("en")}
        className={cn(
          "transition-colors",
          locale === "en"
            ? "font-semibold text-slate-900"
            : "text-slate-400 hover:text-slate-600"
        )}
      >
        EN
      </button>
      <span className="text-slate-300">/</span>
      <button
        onClick={() => onSwitch("pl")}
        className={cn(
          "transition-colors",
          locale === "pl"
            ? "font-semibold text-slate-900"
            : "text-slate-400 hover:text-slate-600"
        )}
      >
        PL
      </button>
    </div>
  );
}

export function MarketingLayout({
  locale: localeProp,
  children,
  navItems: navItemsProp,
}: MarketingLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Resolve effective locale: explicit prop takes precedence, else read from storage
  const [derivedLocale, setDerivedLocale] = useState<Locale>("pl");
  useEffect(() => {
    if (!localeProp) {
      setDerivedLocale(getLocale());
    }
  }, [localeProp]);

  const locale = localeProp ?? derivedLocale;
  const t = getTranslationFn(locale);

  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Determine if we're on a page with dedicated locale routes
  const isLandingPage =
    location.pathname === "/" || location.pathname === "/en";
  const isPricingPage =
    location.pathname === "/pricing" || location.pathname === "/en/pricing";
  const isSecurityPage =
    location.pathname === "/security" || location.pathname === "/en/security";
  const isAboutPage =
    location.pathname === "/about" || location.pathname === "/en/about";

  const landingBase = locale === "en" ? "/en" : "/";
  const anchorPrefix = isLandingPage ? "" : landingBase;
  const pricingPath = locale === "en" ? "/en/pricing" : Paths.pricing();
  const securityPath = locale === "en" ? "/en/security" : Paths.security();
  const aboutPath = locale === "en" ? "/en/about" : Paths.about();

  const defaultNavItems = [
    {
      label: t("Landing.nav.howItWorks"),
      href: `${anchorPrefix}#use-cases`,
    },
    { label: t("Landing.nav.security"), href: securityPath },
  ];

  const navItems = navItemsProp ?? defaultNavItems;

  const handleLanguageSwitch = useCallback(
    (newLocale: Locale) => {
      setLocaleCookie(newLocale);
      updateCurrentLocale(newLocale);

      if (isLandingPage) {
        navigate(newLocale === "en" ? "/en" : "/");
      } else if (isPricingPage) {
        navigate(newLocale === "en" ? "/en/pricing" : "/pricing");
      } else if (isSecurityPage) {
        navigate(newLocale === "en" ? "/en/security" : "/security");
      } else if (isAboutPage) {
        navigate(newLocale === "en" ? "/en/about" : "/about");
      } else {
        // Other marketing pages: update state to re-render with new locale
        setDerivedLocale(newLocale);
      }
    },
    [isLandingPage, isPricingPage, isSecurityPage, isAboutPage, navigate]
  );

  return (
    <div className="flex min-h-screen flex-col w-full overflow-x-clip bg-white font-sans selection:bg-brand-100">
      {/* Navigation */}
      <header
        className={cn(
          "fixed w-full top-0 z-50 transition-all duration-300 border-b border-transparent",
          isScrolled
            ? "bg-white/95 backdrop-blur-md border-slate-100 shadow-sm py-3"
            : "bg-white py-5"
        )}
      >
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          <Link
            to={locale === "en" ? "/en" : "/"}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img
              src="/assets/logo.svg"
              alt="AnyLawyer Logo"
              className="w-[140px]"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              >
                {item.label}
              </Link>
            ))}
            <Link
              to={pricingPath}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
            >
              {t("Landing.nav.pricing")}
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <SignedIn>
              <Button
                asChild
                className="rounded-full px-6 bg-brand-600 text-white hover:bg-brand-700"
              >
                <Link to={Paths.app()}>
                  {t("Landing.nav.goToApp")}{" "}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </SignedIn>
            <SignedOut>
              <SignInButton mode="modal">
                <Button
                  variant="ghost"
                  className="rounded-lg text-slate-600 hover:text-slate-900"
                >
                  {t("Landing.nav.login")}
                </Button>
              </SignInButton>
              <SignInButton mode="modal">
                <Button className="rounded-lg px-6 bg-brand-600 text-white hover:bg-brand-700 shadow-lg shadow-brand-600/20 hover:shadow-brand-600/30 transition-all">
                  {t("Landing.nav.startTrial")}
                </Button>
              </SignInButton>
            </SignedOut>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 text-slate-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white border-b shadow-lg p-4 flex flex-col gap-4">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="text-base font-medium text-slate-600 py-2 border-b border-slate-50"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to={pricingPath}
              className="text-base font-medium text-slate-600 py-2 border-b border-slate-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("Landing.nav.pricing")}
            </Link>
            <div className="flex flex-col gap-3 mt-2">
              <SignedOut>
                <SignInButton mode="modal">
                  <Button
                    variant="outline"
                    className="w-full justify-center rounded-lg"
                  >
                    {t("Landing.nav.login")}
                  </Button>
                </SignInButton>
                <SignInButton mode="modal">
                  <Button className="w-full justify-center rounded-lg bg-brand-600 text-white hover:bg-brand-700">
                    {t("Landing.nav.startTrial")}
                  </Button>
                </SignInButton>
              </SignedOut>
              <SignedIn>
                <Button
                  asChild
                  className="w-full justify-center rounded-lg bg-brand-600 text-white hover:bg-brand-700"
                >
                  <Link to={Paths.app()}>{t("Landing.nav.goToApp")}</Link>
                </Button>
              </SignedIn>
            </div>
          </div>
        )}
      </header>

      <main key={locale} className="flex-1 pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-5 gap-12 mb-12">
            <div className="col-span-1">
              <img src="/assets/logo.svg" alt="Logo" className="w-32 mb-6" />
              <p className="text-slate-500 text-sm leading-relaxed">
                {t("Landing.footer.tagline")}
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4 text-sm">
                {t("Landing.footer.product")}
              </h4>
              <ul className="space-y-2.5 text-sm text-slate-500">
                <li>
                  <Link
                    to={`${anchorPrefix}#use-cases`}
                    className="hover:text-slate-900 transition-colors"
                  >
                    {t("Landing.nav.howItWorks")}
                  </Link>
                </li>
                <li>
                  <Link
                    to={pricingPath}
                    className="hover:text-slate-900 transition-colors"
                  >
                    {t("Landing.nav.pricing")}
                  </Link>
                </li>
                <li>
                  <Link
                    to={securityPath}
                    className="hover:text-slate-900 transition-colors"
                  >
                    {t("Landing.nav.security")}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4 text-sm">
                {t("Landing.footer.resources")}
              </h4>
              <ul className="space-y-2.5 text-sm text-slate-500">
                <li>
                  <Link
                    to={Paths.docs()}
                    className="hover:text-slate-900 transition-colors"
                  >
                    {t("Landing.footer.docs")}
                  </Link>
                </li>
                <li>
                  <Link
                    to={Paths.eurekaSeoIndex()}
                    className="hover:text-slate-900 transition-colors"
                  >
                    {t("Landing.footer.taxInterpretations")}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4 text-sm">
                {t("Landing.footer.company")}
              </h4>
              <ul className="space-y-2.5 text-sm text-slate-500">
                <li>
                  <Link
                    to={aboutPath}
                    className="hover:text-slate-900 transition-colors"
                  >
                    {t("Landing.footer.aboutUs")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="mailto:hello@anylawyer.com"
                    className="hover:text-slate-900 transition-colors"
                  >
                    {t("Landing.footer.contactUs")}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4 text-sm">
                {t("Landing.footer.legal")}
              </h4>
              <ul className="space-y-2.5 text-sm text-slate-500">
                <li>
                  <Link
                    to={Paths.privacyPolicy()}
                    className="hover:text-slate-900 transition-colors"
                  >
                    {t("Landing.footer.privacyPolicy")}
                  </Link>
                </li>
                <li>
                  <Link
                    to={Paths.termsOfService()}
                    className="hover:text-slate-900 transition-colors"
                  >
                    {t("Landing.footer.termsOfService")}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-400">
              {t("Landing.footer.copyright")}
            </p>
            <LanguageSwitcher locale={locale} onSwitch={handleLanguageSwitch} />
          </div>
        </div>
      </footer>
    </div>
  );
}
